import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Navbar from '../../../../ReusableBlocks/Navbar';
import { DEFAULT_PARTNER_FORM_OPTIONS } from '../Constants/partnerFormOptions';
import { PARTNER_PORTAL_MY_PROJECTS, PARTNER_PORTAL_NOT_AUTHORIZED } from '../Constants/partnerRoutes';
import { usePartnerFormOptions } from '../Hooks/usePartnerFormOptions';
import { usePartnerProjectDetails } from '../Hooks/usePartnerProjectDetails';
import { usePartnerProjects } from '../Hooks/usePartnerProjects';
import { usePartnerRoleAccess } from '../Hooks/usePartnerRoleAccess';
import PartnerLayout from '../Layout/PartnerLayout';
import PageHeader from '../Components/shared/PageHeader';
import PartnerDateInput from '../Components/forms/PartnerDateInput';
import ProjectStatusTimeline from '../Components/timeline/ProjectStatusTimeline';
import {
  shouldPromptForProjectLeadEmailCorrection,
  validatePartnerProjectUpdatePayload,
} from '../Utils/partnerValidation';
import { mapProjectToForm } from '../Storage/partnerMappers';
import { formatPartnerDisplayDate } from '../Utils/partnerDateUtils';
import { promptForValidProjectLeadEmail } from '../Utils/partnerEmailPrompt';
import { mergeProjectWithTimeline } from '../Utils/partnerTimelineUtils';
import {
  canPartnerChangeProjectStatus,
  getPaeQueueStatusBadgeMeta,
  getPartnerProjectStatusOptions,
  INFO_REQUESTED_STATUS,
  resolveUpdatedProjectStatus,
  validatePartnerStatusUpdate,
} from '../Utils/partnerProjectStatusUtils';
import {
  getPartnerApiErrorMessage,
  PartnerApiError,
  showPartnerProjectUpdatedPopup,
  showPartnerValidationErrorPopup,
} from '../Utils/partnerApiFeedback';

const ProjectDetailsPage = ({ projectId: projectIdProp, inline = false, onBackToProjects }) => {
  const access = usePartnerRoleAccess();
  const { id: routeProjectId } = useParams();
  const projectId = projectIdProp || routeProjectId;
  const { updateProject } = usePartnerProjects();
  const { formOptions } = usePartnerFormOptions();
  const { project, loading, error, setProject } = usePartnerProjectDetails(projectId);
  const options = formOptions || DEFAULT_PARTNER_FORM_OPTIONS;
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [updateMessage, setUpdateMessage] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (project) {
      setForm(mapProjectToForm(project));
      setErrors({});
      setUpdateMessage('');
    }
  }, [project]);

  const projectStatusOptions = useMemo(() => (
    getPartnerProjectStatusOptions(options.projectStatus, project?.projectStatus)
  ), [options.projectStatus, project?.projectStatus]);

  const isInfoRequested = project?.projectStatus === INFO_REQUESTED_STATUS;
  const heroStatusMeta = useMemo(
    () => getPaeQueueStatusBadgeMeta(project),
    [project],
  );

  if (!access.canAccessPartnerPages) {
    return <Navigate to={PARTNER_PORTAL_NOT_AUTHORIZED} replace />;
  }

  const detailView = (content) => {
    if (inline) {
      return content;
    }

    return (
      <>
        <Navbar activeTab="PARTNER_PORTAL" />
        <PartnerLayout access={access}>
          {content}
        </PartnerLayout>
      </>
    );
  };

  if (loading) {
    return detailView(<p className="partner-loading-text">Loading project details...</p>);
  }

  if (!project || !form) {
    if (inline) {
      return (
        <div className="partner-project-detail">
          <button className="partner-project-detail-back btn btn-link p-0" type="button" onClick={onBackToProjects}>
            Back to My Projects
          </button>
          <p className="text-danger">{error || 'Project details could not be loaded.'}</p>
        </div>
      );
    }
    return <Navigate to={PARTNER_PORTAL_MY_PROJECTS} replace />;
  }

  const products = form.productsInScope || [];
  const submittedAt = project.submittedAt;

  const updateField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }
      const nextErrors = { ...previous };
      delete nextErrors[field];
      return nextErrors;
    });
    setUpdateMessage('');
  };

  const onToggleProduct = (product) => {
    const selected = new Set(form.productsInScope);
    if (selected.has(product)) {
      selected.delete(product);
    } else {
      selected.add(product);
    }
    updateField('productsInScope', Array.from(selected));
  };

  const handleUpdate = async () => {
    let workingForm = { ...form };

    if (shouldPromptForProjectLeadEmailCorrection(workingForm.projectLeadEmail)) {
      const correctedEmail = await promptForValidProjectLeadEmail(workingForm.projectLeadEmail);
      if (!correctedEmail) {
        setErrors({
          projectLeadEmail: 'Enter a valid email with @ and . (e.g. name@company.com)',
        });
        setUpdateMessage('');
        return;
      }
      workingForm = { ...workingForm, projectLeadEmail: correctedEmail };
      setForm(workingForm);
    }

    const check = validatePartnerProjectUpdatePayload(workingForm);
    const statusCheck = validatePartnerStatusUpdate(workingForm, project.projectStatus);
    const mergedErrors = {
      ...check.errors,
      ...statusCheck.errors,
    };

    if (!check.isValid || !statusCheck.isValid) {
      setErrors(mergedErrors);
      setUpdateMessage('');
      return;
    }

    setErrors({});
    setUpdating(true);

    try {
      const requestedStatus = workingForm.projectStatus;
      const updated = await updateProject(project.id, workingForm);
      const resolvedStatus = resolveUpdatedProjectStatus(updated, requestedStatus);
      const mergedProject = {
        ...project,
        ...updated,
        projectStatus: resolvedStatus,
      };

      setProject((current) => mergeProjectWithTimeline(current, mergedProject));
      setForm(mapProjectToForm(mergedProject));
      setUpdateMessage('');
      await showPartnerProjectUpdatedPopup();
    } catch (updateError) {
      if (updateError instanceof PartnerApiError && Object.keys(updateError.fieldErrors).length > 0) {
        setErrors(updateError.fieldErrors);
        setUpdateMessage('Please fix the highlighted fields below.');
        await showPartnerValidationErrorPopup({
          title: 'Unable to update project',
          fieldErrors: updateError.fieldErrors,
        });
        return;
      }

      setUpdateMessage(getPartnerApiErrorMessage(updateError, 'Failed to update project.'));
    } finally {
      setUpdating(false);
    }
  };

  const backControl = onBackToProjects ? (
    <button className="partner-project-detail-back btn btn-link p-0" type="button" onClick={onBackToProjects}>
      Back to My Projects
    </button>
  ) : (
    <Link className="partner-project-detail-back" to={PARTNER_PORTAL_MY_PROJECTS}>
      Back to My Projects
    </Link>
  );

  return detailView(
    <div className="partner-project-detail partner-project-detail-page">
      <div className="partner-project-detail-nav">
        {backControl}
      </div>

      {error ? <p className="text-danger">{error}</p> : null}
      {isInfoRequested ? (
        <p className="partner-info-requested-banner">
          PAE has requested more information. Update your project details and choose a new project status below.
        </p>
      ) : null}

      <PageHeader
        title={form.customerName || project.customerName}
        description={`${products.join(' · ') || '-'} · ${form.engagementType || '-'} · ${form.deploymentModel || '-'} · Submitted ${formatPartnerDisplayDate(submittedAt)}`}
        rightContent={(
          <span
            className={`partner-project-hero-status-badge ${heroStatusMeta.className || ''}`.trim()}
            role="status"
          >
            <span className="partner-project-hero-status-badge-label">Project status</span>
            <span className="partner-project-hero-status-badge-main">
              <span className="partner-project-hero-status-dot" aria-hidden="true" />
              {heroStatusMeta.label || form.projectStatus}
            </span>
          </span>
        )}
      />

      <div className="partner-project-detail-workspace">
        <div className="partner-project-detail-grid">
          <div className="partner-project-detail-main">
            <section className="partner-project-panel partner-project-panel--themed">
              <header className="partner-project-panel-header partner-project-panel-header--themed">
                <span className="partner-project-panel-step" aria-hidden="true">1</span>
                <span>Partner and customer</span>
              </header>
              <div className="partner-project-panel-body partner-project-fields-grid partner-project-fields-grid--detail">
                  <div className="partner-project-field">
                    <label>Partner name</label>
                    <div className="partner-project-field-readonly">{form.partnerName || '-'}</div>
                  </div>
                  <div className="partner-project-field">
                    <label>Partner company</label>
                    <div className="partner-project-field-readonly">
                      <div className="psm-queue-main-cell">
                        <strong>{project.companyName || '-'}</strong>
                        <span>{project.csn || '-'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="partner-project-field">
                    <label>Partner CSN</label>
                    <div className="partner-project-field-readonly">{form.csn || '-'}</div>
                  </div>
                  <div className="partner-project-field">
                    <label>Customer name</label>
                    <input
                      className="form-control"
                      value={form.customerName}
                      onChange={(e) => updateField('customerName', e.target.value)}
                    />
                    {errors.customerName ? <small className="text-danger">{errors.customerName}</small> : null}
                  </div>
                  <div className="partner-project-field">
                    <label>Partner Project lead name</label>
                    <input
                      className="form-control"
                      value={form.projectLeadName}
                      onChange={(e) => updateField('projectLeadName', e.target.value)}
                    />
                    {errors.projectLeadName ? <small className="text-danger">{errors.projectLeadName}</small> : null}
                  </div>
                  <div className="partner-project-field">
                    <label>Partner Project lead email</label>
                    <input
                      className="form-control"
                      value={form.projectLeadEmail}
                      onChange={(e) => updateField('projectLeadEmail', e.target.value)}
                    />
                    {errors.projectLeadEmail ? <small className="text-danger">{errors.projectLeadEmail}</small> : null}
                  </div>
                </div>
              </section>

            <section className="partner-project-panel partner-project-panel--themed">
              <header className="partner-project-panel-header partner-project-panel-header--themed">
                <span className="partner-project-panel-step" aria-hidden="true">2</span>
                <span>Project details</span>
              </header>
              <div className="partner-project-panel-body">
                <div className="partner-project-field partner-project-field-full">
                  <label>Products in scope</label>
                  <div className={`partner-products-grid partner-products-grid--modern partner-products-grid--detail ${errors.productsInScope ? 'has-error' : ''}`}>
                    {options.productsInScope.map((product) => {
                      const isSelected = form.productsInScope.includes(product);
                      return (
                        <button
                          key={product}
                          type="button"
                          className={`partner-chip partner-chip--modern ${isSelected ? 'is-selected' : ''}`}
                          aria-pressed={isSelected}
                          onClick={() => onToggleProduct(product)}
                        >
                          <span className="partner-chip-mark" aria-hidden="true">{isSelected ? '✓' : '+'}</span>
                          {product}
                        </button>
                      );
                    })}
                  </div>
                  {errors.productsInScope ? <small className="text-danger">{errors.productsInScope}</small> : null}
                </div>
                <div className="partner-project-fields-grid partner-project-fields-grid-three partner-project-fields-grid--detail">
                    <div className="partner-project-field">
                      <label>Engagement type</label>
                      <select
                        className="partner-form-select form-select"
                        value={form.engagementType}
                        onChange={(e) => updateField('engagementType', e.target.value)}
                      >
                        <option value="">Select</option>
                        {options.engagementTypes.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                      {errors.engagementType ? <small className="text-danger">{errors.engagementType}</small> : null}
                    </div>
                    <div className="partner-project-field">
                      <label>Deployment model</label>
                      <select
                        className="partner-form-select form-select"
                        value={form.deploymentModel}
                        onChange={(e) => updateField('deploymentModel', e.target.value)}
                      >
                        <option value="">Select</option>
                        {options.deploymentModels.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                      {errors.deploymentModel ? <small className="text-danger">{errors.deploymentModel}</small> : null}
                    </div>
                    <div className="partner-project-field">
                      <label>Project status</label>
                      <select
                        className={`partner-form-select form-select ${errors.projectStatus ? 'is-invalid' : ''}`}
                        value={form.projectStatus}
                        onChange={(e) => updateField('projectStatus', e.target.value)}
                      >
                        {projectStatusOptions.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                      {canPartnerChangeProjectStatus(project.projectStatus) ? (
                        <small className="partner-field-hint">
                          Choose a new status after updating the requested information.
                        </small>
                      ) : null}
                      {errors.projectStatus ? <small className="text-danger">{errors.projectStatus}</small> : null}
                    </div>
                  </div>
                <div
                  className={[
                    'partner-project-date-row',
                    'partner-project-date-row-four',
                    form.completedDate ? 'partner-project-date-row-with-completed' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <div className="partner-project-field">
                    <label>Est. start date</label>
                    <PartnerDateInput
                      className={`form-control ${errors.estimatedStartDate ? 'is-invalid' : ''}`}
                      value={form.estimatedStartDate}
                      onChange={(nextValue) => updateField('estimatedStartDate', nextValue)}
                    />
                    {errors.estimatedStartDate ? <small className="text-danger">{errors.estimatedStartDate}</small> : null}
                  </div>
                  <div className="partner-project-field partner-project-field--actual-date">
                    <label>Actual start date</label>
                    <PartnerDateInput
                      className={`form-control ${errors.actualStartDate ? 'is-invalid' : ''}`}
                      value={form.actualStartDate}
                      onChange={(nextValue) => updateField('actualStartDate', nextValue)}
                    />
                    {errors.actualStartDate ? <small className="text-danger">{errors.actualStartDate}</small> : null}
                  </div>
                  <div className="partner-project-field">
                    <label>Est. go-live date</label>
                    <PartnerDateInput
                      className={`form-control ${errors.estimatedGoliveDate ? 'is-invalid' : ''}`}
                      value={form.estimatedGoliveDate}
                      onChange={(nextValue) => updateField('estimatedGoliveDate', nextValue)}
                    />
                    {errors.estimatedGoliveDate ? <small className="text-danger">{errors.estimatedGoliveDate}</small> : null}
                  </div>
                  <div className="partner-project-field partner-project-field--actual-date">
                    <label>Actual go-live date</label>
                    <PartnerDateInput
                      className={`form-control ${errors.actualGoliveDate ? 'is-invalid' : ''}`}
                      value={form.actualGoliveDate}
                      onChange={(nextValue) => updateField('actualGoliveDate', nextValue)}
                    />
                    {errors.actualGoliveDate ? <small className="text-danger">{errors.actualGoliveDate}</small> : null}
                  </div>
                  {form.completedDate ? (
                    <div className="partner-project-field partner-project-field--completed-date">
                      <label>Completed date</label>
                      <div className="partner-project-field-readonly">
                        {formatPartnerDisplayDate(form.completedDate)}
                      </div>
                    </div>
                  ) : null}
                </div>
                  <div className="partner-project-detail-notes-block">
                  <div className="partner-project-field partner-project-field-full">
                    <label>Project description</label>
                    <textarea
                      className={`form-control ${errors.projectDescription ? 'is-invalid' : ''}`}
                      rows={2}
                      value={form.projectDescription}
                      onChange={(e) => updateField('projectDescription', e.target.value)}
                    />
                    {errors.projectDescription ? <small className="text-danger">{errors.projectDescription}</small> : null}
                  </div>
                  <div className="partner-project-fields-grid partner-project-fields-grid--detail partner-project-notes-row">
                    <div className="partner-project-field">
                      <label>Competitive context</label>
                      <select
                        className="partner-form-select form-select"
                        value={form.competitiveContext}
                        onChange={(e) => updateField('competitiveContext', e.target.value)}
                      >
                        <option value="">Select if applicable</option>
                        {options.competitiveContexts.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    </div>
                    <div className="partner-project-field">
                      <label>Additional notes for your PAE</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={form.additionalNotes}
                        onChange={(e) => updateField('additionalNotes', e.target.value)}
                      />
                    </div>
                  </div>
                  </div>
                </div>
              </section>
          </div>

          <aside className="partner-project-timeline-card partner-project-timeline-card--themed">
            <header className="partner-project-panel-header partner-project-panel-header--themed partner-project-timeline-header">
              <span className="partner-project-panel-step partner-project-panel-step--timeline" aria-hidden="true">3</span>
              <span>Project status</span>
            </header>
            <div className="partner-project-panel-body">
              <ProjectStatusTimeline project={project} />
            </div>
          </aside>
        </div>

        <div className={`partner-form-submit-bar partner-form-submit-bar--modern partner-project-detail-update-bar ${Object.keys(errors).length > 0 || updateMessage ? 'partner-form-submit-bar--error' : ''}`}>
          <div className="partner-form-submit-status">
            <span className="partner-form-status-icon" aria-hidden="true" />
            <span className={`partner-form-complete-indicator ${Object.keys(errors).length > 0 || updateMessage ? 'has-error' : ''}`}>
              {updateMessage || (Object.keys(errors).length > 0
                ? 'Please fix the highlighted fields below.'
                : 'Save changes to send updates to your PAE')}
            </span>
          </div>
          <div className="partner-submit-actions">
            <button
              className="btn btn-primary partner-submit-btn"
              type="button"
              disabled={updating}
              onClick={handleUpdate}
            >
              {updating ? 'Updating…' : 'Update project'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailsPage;
